import {Component, Inject, Injector, ViewChild, ChangeDetectionStrategy} from "@angular/core";

import {ReportError} from "app/app.service";
import {ExecutionType} from "app/aws-ssm/automation-document/document-execution-dialog/input-parameters/input-parameters.component";
import {AwsSsmRedirectionScope, ConcurrencyOptions, ErrorOptions, ExecuteDocumentState, ExecuteResourcePayload, ExecuteResourceValues, InputParametersState, TagObject, VersionListBatchResponse} from "app/services/domain/aws-ssm-document.service";
import {BaseApplicationComponent} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";

import {BaseComponent} from "framework/ui/components";
import {ControlOption} from "framework/ui/control-option";
import {OVERLAY_DATA, OverlayDialogRef} from "framework/ui/overlays/overlay-dialog";
import {OverlayComponent, OverlayConfig} from "framework/ui/overlays/overlay.component";
import {StandardFormComponent} from "framework/ui/overlays/standard-form.component";
import {Future} from "framework/utils/concurrency";

@Component({
               templateUrl: "./document-execution-dialog.component.html",
               styleUrls  : ["./document-execution-dialog.component.scss"],
               changeDetection: ChangeDetectionStrategy.Eager,
               standalone : false
           })
export class DocumentExecutionDialogComponent extends BaseApplicationComponent
{
    public readonly currentDocument: Models.AwsSsmDocument;
    public ExecutionType                                                 = ExecutionType;
    public executeRequest: Models.AwsSsmExecuteAutomationDocumentRequest = Models.AwsSsmExecuteAutomationDocumentRequest.newInstance({});
    public inputParametersState: InputParametersState                    = {
        formattedModelValue: "{}",
        currentModelValue  : "{}",
        valid              : false
    };
    public parameterOptions: ControlOption<string>[]                     = [];
    public filterOptions: Models.AwsSsmDocumentFilter;
    public targetOptions: ControlOption<string>[]                        = [];
    public versionDetails: VersionListBatchResponse;
    public versionRecord: Models.AwsSsmDocumentVersionDetails;
    public versionOptions: ControlOption<string>[]                       = [];
    public regionOptions: ControlOption<string>[]                        = [];
    public resGrpOptions: ControlOption<string>[]                        = [];
    public cloudAccountOptions: ControlOption<string>[]                  = [];
    public versionLoader: boolean;
    public executeLoader: boolean;
    public executeDocumentObj: ExecuteDocumentModel                      = {
        maxConcurrencyTarget    : "",
        maxConcurrencyPercentage: "",
        maxErrors               : "",
        maxErrorsPercentage     : "",
        isTagDuplicate          : false,
        isInpParamsValid        : true,
        tagKey                  : "",
        tagsList                : [],
        tagValue                : "",
        targetObj               : {
            resourceGroup: "",
            allInstances : "*",
            tagsList     : [],
            tagKey       : "",
            tagValue     : "",
            tagValidate  : false
        }
    };

    @ViewChild(StandardFormComponent) form: StandardFormComponent;

    constructor(inj: Injector,
                public dialogRef: OverlayDialogRef<Models.AwsSsmDocument>,
                @Inject(OVERLAY_DATA) public data: ExecuteDocumentState)
    {
        super(inj);
        this.currentDocument                 = data.initial;
        this.executeDocumentObj.type         = ExecutionType.Simple;
        this.executeRequest.cloud_account_id = "";
        this.executeRequest.region           = "";
        this.filterOptions                   = data.filterValues;
        this.cloudAccountOptions             = this.filterOptions.cloud_account_details.map(cloud_account => new ControlOption<string>(cloud_account.id, cloud_account.name));
        this.regionOptions                   = this.filterOptions.regions.map(region => new ControlOption<string>(region, region));
        this.executeRequest.region           = this.currentDocument.region || "";
        this.executeRequest.cloud_account_id = this.currentDocument.cloud_account_id || "";
        this.initVersionOptions();
    }

    public static async open(comp: BaseComponent,
                             data: ExecuteDocumentState): Promise<Models.AwsSsmDocument>
    {
        return OverlayComponent.open(comp, DocumentExecutionDialogComponent, {
            data  : data,
            config: OverlayConfig.dialog({
                                             showCloseButton    : true,
                                             width              : "70vw",
                                             height             : "80vh",
                                             closableViaBackdrop: false
                                         })
        });
    }

    public async initVersionOptions(): Promise<void>
    {
        this.versionLoader = true;
        try
        {
            this.versionDetails = await this.app.domain.awsSsmDocument.getVersionList(this.currentDocument.sysId);
            this.versionOptions = this.versionDetails.listRecord
                                      .map((versionObj) =>
                                           {
                                               const versionPrefixString = versionObj.document_version === this.currentDocument.document_version ? "- (Default)" : "";
                                               return new ControlOption<string>(versionObj.document_version, `v${versionObj.document_version} ${versionPrefixString}`);
                                           })
                                      .sort((a,
                                             b) => parseInt(a.id) - parseInt(b.id));

            this.executeRequest.document_version = this.currentDocument.document_version;
            this.findVersionRecord();
        }
        finally
        {
            this.versionLoader = false;
        }
    }

    public findVersionRecord(): void
    {
        this.versionRecord              = this.app.domain.awsSsmDocument.getVersionRecord(this.executeRequest.document_version);
        this.inputParametersState.valid = true;
        if (this.versionRecord?.parameters)
        {
            this.inputParametersState.valid = false;
            this.resetParams(true);
        }
    }

    public addTags()
    {
        const found = this.executeDocumentObj.tagsList.some(el => el.key === this.executeDocumentObj.tagKey);
        if (!found)
        {
            this.executeDocumentObj.isTagDuplicate = false;
            let tagObj: TagObject                  = {
                key  : this.executeDocumentObj.tagKey,
                value: this.executeDocumentObj.tagValue
            };
            this.executeDocumentObj.tagsList.push(Models.KeyValuePair.newInstance(tagObj));
            this.executeDocumentObj.tagKey   = "";
            this.executeDocumentObj.tagValue = "";
        }
        else
        {
            this.executeDocumentObj.isTagDuplicate = true;
        }
    }

    public addTargetTags(): void
    {
        let tagObj: TagObject = {
            key  : this.executeDocumentObj.targetObj.tagKey,
            value: this.executeDocumentObj.targetObj.tagValue
        };
        this.executeDocumentObj.targetObj.tagsList.push(Models.KeyValuePair.newInstance(tagObj));
        this.executeDocumentObj.targetObj.tagValidate = false;
        this.executeDocumentObj.targetObj.tagKey      = "";
        this.executeDocumentObj.targetObj.tagValue    = "";
    }

    public removeTags(tagObj: Models.KeyValuePair,
                      type: string): void
    {
        let tags    = type === "target" ? this.executeDocumentObj.targetObj.tagsList : this.executeDocumentObj.tagsList;
        const index = tags.indexOf(tagObj);
        if (index >= 0)
        {
            tags.splice(index, 1);
        }
    }

    public isFormValid(): boolean
    {
        if (!this.form?.form.valid) return false;
        if (this.executeDocumentObj.targetObj.tagValidate) return false;
        if (!this.executeDocumentObj.isInpParamsValid) return false;
        if (this.versionLoader) return false;
        return true;
    }

    public async resetParams(useDefaultValues: boolean): Promise<void>
    {
        const parameters: Models.AwsSsmParameter[] = [...this.versionRecord.parameters];
        this.executeDocumentObj.isInpParamsValid   = true;
        this.versionRecord.parameters              = [];
        await Future.delayed(20);
        let paramDefaultValues = JSON.parse(this.inputParametersState.formattedModelValue);
        let currentModelValue  = JSON.parse(this.inputParametersState.currentModelValue);
        if (!useDefaultValues)
        {
            const targetParameterName: string       = this.executeRequest.target_parameter_name;
            paramDefaultValues[targetParameterName] = "";
            currentModelValue[targetParameterName]  = "";
        }
        this.inputParametersState     = {
            formattedModelValue: JSON.stringify(paramDefaultValues),
            currentModelValue  : JSON.stringify(currentModelValue),
            valid              : false
        };
        this.versionRecord.parameters = parameters;
        if (this.executeDocumentObj.type === ExecutionType.RateControl && this.versionRecord?.parameters?.length > 0)
        {
            this.parameterOptions = this.versionRecord.parameters.map(params => new ControlOption<string>(params.name, params.name));
        }
    }

    public async fetchResourceGroup()
    {
        const cloudAccountExists = !!this.executeRequest.cloud_account_id;
        const regionExists       = !!this.executeRequest.region;
        if (cloudAccountExists && regionExists)
        {
            const reqObj: ExecuteResourcePayload = {
                type            : ExecuteResourceValues.ResourceGroup,
                region          : this.executeRequest.region,
                cloud_account_id: this.executeRequest.cloud_account_id,
                filterOptions   : this.filterOptions
            };

            const resGrpSummary = await this.app.domain.awsSsmDocument.fetchInventoryDetails(reqObj);
            this.resGrpOptions  = resGrpSummary.map(roleObj => new ControlOption<string>(roleObj.summary_details.GroupName, roleObj.summary_details.GroupName));
        }
    }

    public triggerTargetOptions(): void
    {
        const executeDocumentObj = this.executeDocumentObj;
        if (executeDocumentObj.selectedTargetValue)
        {
            executeDocumentObj.targetObj.tagValidate = false;
            executeDocumentObj.isInpParamsValid      = true;
            let useDefaultValues                     = false;
            switch (executeDocumentObj.selectedTargetValue)
            {
                case TargetScopes.Tags:
                    executeDocumentObj.targetObj.tagValidate = true;
                    break;

                case TargetScopes.ParameterValues:
                    useDefaultValues                    = true;
                    executeDocumentObj.isInpParamsValid = false;
                    break;

                case TargetScopes.ResourceGroup:
                    this.fetchResourceGroup();
                    break;
            }
            if (this.versionRecord?.parameters)
            {
                this.resetParams(useDefaultValues);
            }
        }
    }

    public onDocumentChange(): void
    {
        if (this.executeDocumentObj.type === ExecutionType.RateControl && this.executeDocumentObj.selectedTargetValue === TargetScopes.ResourceGroup)
        {
            this.fetchResourceGroup();
        }
    }

    public onExecutionTypeChange(type: ExecutionType): void
    {
        if (this.versionRecord?.parameters)
        {
            this.resetParams(true);
        }
        if (type === ExecutionType.RateControl)
        {
            this.executeDocumentObj.maxConcurrencyOption     = ConcurrencyOptions.Targets;
            this.executeDocumentObj.maxErrorsOption          = ErrorOptions.Errors;
            this.executeDocumentObj.maxConcurrencyTarget     = "";
            this.executeDocumentObj.maxConcurrencyPercentage = "";
            this.executeDocumentObj.maxErrorsPercentage      = "";
            this.executeDocumentObj.maxErrors                = "";
            this.targetOptions                               = [
                new ControlOption<string>(TargetScopes.ParameterValues, TargetScopes.ParameterValues),
                new ControlOption<string>(TargetScopes.ResourceGroup, TargetScopes.ResourceGroup),
                new ControlOption<string>(TargetScopes.AllInstances, TargetScopes.AllInstances),
                new ControlOption<string>(TargetScopes.Tags, TargetScopes.Tags)
            ];
        }
    }

    @ReportError
    public async executeDocument(): Promise<void>
    {
        this.executeRequest.parameters    = JSON.parse(this.inputParametersState.formattedModelValue);
        this.executeRequest.document_name = this.currentDocument.name;
        this.executeRequest.tags          = this.executeDocumentObj.tagsList;
        for (const param of this.versionRecord.parameters)
        {
            const parameter = this.executeRequest.parameters?.[param.name];
            if (param.type == Models.AwsSsmDocumentParameterType.MapList && parameter)
            {
                for (const [index, value] of parameter.entries())
                {
                    parameter[index] = JSON.stringify(value);
                }
            }
        }
        if (this.executeDocumentObj.type === ExecutionType.RateControl)
        {
            const isConcurrencyTarget: boolean = this.executeDocumentObj.maxConcurrencyOption === ConcurrencyOptions.Targets;
            const isErrorOptions: boolean      = this.executeDocumentObj.maxErrorsOption === ErrorOptions.Errors;

            this.executeRequest.max_concurrency = isConcurrencyTarget ? this.executeDocumentObj.maxConcurrencyTarget.toString() : this.executeDocumentObj.maxConcurrencyPercentage + "%";
            this.executeRequest.max_errors      = isErrorOptions ? this.executeDocumentObj.maxErrors.toString() : this.executeDocumentObj.maxErrorsPercentage + "%";
            let targetObj;
            switch (this.executeDocumentObj.selectedTargetValue)
            {
                case TargetScopes.ParameterValues:
                    let paramValue = this.executeRequest.parameters[this.executeRequest.target_parameter_name];
                    delete this.executeRequest.parameters[this.executeRequest.target_parameter_name];
                    targetObj = {
                        key   : this.executeDocumentObj.selectedTargetValue,
                        values: paramValue
                    };
                    break;

                case TargetScopes.ResourceGroup:
                    targetObj = {
                        key   : this.executeDocumentObj.selectedTargetValue,
                        values: [this.executeDocumentObj.targetObj.resourceGroup]
                    };
                    break;

                case TargetScopes.AllInstances:
                    targetObj = {
                        key   : "InstanceIds",
                        values: [this.executeDocumentObj.targetObj.allInstances]
                    };
                    break;

                default:
                    targetObj = {
                        key   : "tag:" + this.executeDocumentObj.targetObj.tagsList[0].key,
                        values: [this.executeDocumentObj.targetObj.tagsList[0].value]
                    };
                    break;
            }
            this.executeRequest.targets = [Models.AwsSsmCommandDocumentTarget.newInstance(targetObj)];
        }
        this.executeLoader = true;
        try
        {
            await this.app.domain.apis.awsSsmAutomation.automationExecute(this.app.domain.users.lastActiveTenantId, this.currentDocument.sysId, this.executeRequest);
            this.app.framework.errors.success("Document Executed Successfully", -1);
            this.app.ui.navigation.go("provider/services/aws-ssm", [AwsSsmRedirectionScope.AutomationExecution]);
        }
        finally
        {
            this.executeLoader = false;
        }
    }

    protected readonly ConcurrencyOptions = ConcurrencyOptions;
    protected readonly ErrorOptions       = ErrorOptions;
}

export enum TargetScopes
{
    AllInstances    = "All Instances",
    ParameterValues = "ParameterValues",
    ResourceGroup   = "ResourceGroup",
    Tags            = "Tags"
}

export interface ExecuteDocumentModel
{
    maxConcurrencyOption?: ConcurrencyOptions;
    maxConcurrencyTarget: string;
    maxConcurrencyPercentage: string;
    maxErrorsOption?: ErrorOptions;
    maxErrors: string;
    maxErrorsPercentage: string;
    isInpParamsValid: boolean;
    isTagDuplicate: boolean;
    selectedTargetValue?: TargetScopes;
    tagKey: string;
    tagsList: Models.KeyValuePair[];
    tagValue: string;
    targetObj: ExecuteDocumentTargetModel;
    type?: ExecutionType;
}

export interface ExecuteDocumentTargetModel
{
    resourceGroup: string;
    allInstances: "*";
    tagsList: Models.KeyValuePair[];
    tagKey: string;
    tagValue: string;
    tagValidate: boolean;
}
